/**
 * useTodaysSchedule(date)
 *
 * Fetches the viewer's visible schedule rows for a given date from
 * Supabase's `notes-c` table. Visibility mirrors `SchedulesTable.getSchedules`:
 *
 *   - admins + doctors: see ALL rows for the date (RLS lets them);
 *     no extra filter is applied, and the fetch FANS OUT across every schema
 *     ticked in the Providers dropdown, tagging each row `_schema`.
 *   - everyone else (tenant users + medstudents): three-way OR —
 *       1. own rows                         (`user_id.eq.<me>`)
 *       2. per-row shared rows              (`shared_users` overlap with my id)
 *       3. share-all owners' rows           (`user_id.in.(<ownerIds>)`)
 *     Single schema, exactly as before.
 *
 * Without the role split, doctors-on-the-clinic-page saw an empty Active /
 * Completed sidebar because the tenant OR clauses limited them to their own
 * user_id while their schedule rows actually belong to other users.
 *
 * The fan-out mirrors SchedulesTable.getSchedules — same `selectedProviders`
 * source, same clinical-role gate, same `_schema` tag. Without it the clinic
 * page's sidebar read only the viewer's home schema, so a doctor who had
 * ticked other providers saw those patients on /schedules and then lost them
 * the moment they opened a chart.
 *
 * Returns: { schedules, isLoading, error, refetch }
 */

import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { supabaseUrl, supabaseKey, getBackendURL } from '../../components/env';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import { fetchEffectiveUserRow } from '../../utils/effectiveUserRole';
import { sharedUsersOverlapOrClauses } from '../../utils/shareAccess';
import { useSelectedProviders } from '../useSelectedProviders';
import { hydrateScheduleRowsTimezoneSource, fetchViewerIanaTimezone } from '../../utils/scheduleRowTimezone';
import {
  isPatientRole,
  filterRowsForPatient,
  hasClinicalAdminPermissions,
} from '../../config/roles';

function getSchedulesEndpoint() {
  const base = supabaseUrl || '';
  return `${base}/rest/v1/notes-c`;
}

async function getSessionInfo() {
  try {
    const supabase = await getSupabase();
    const { data } = await supabase.auth.getSession();
    const userId = data?.session?.user?.id ?? null;
    const email = data?.session?.user?.email ?? null;
    let role = null;
    let userRow = null;
    // Query the active schema first, falling back to public.users if
    // the query fails or returns no data (e.g. schema cache issues).
    if (userId) {
      try {
        const userRes = await fetchEffectiveUserRow({ id: userId });
        userRow = userRes.data || null;
        role = userRow?.role ?? null;
      } catch (_) {
        role = null;
        userRow = null;
      }
    }

    return {
      token: data?.session?.access_token ?? null,
      userId,
      email,
      role,
      userRow,
    };
  } catch {
    return { token: null, userId: null, email: null, role: null, userRow: null };
  }
}

async function fetchShareAllOwnerIds(userId) {
  if (!userId) return [];
  try {
    const backend = getBackendURL();
    const res = await fetch(
      `${backend}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(userId)}`,
    );
    if (!res.ok) return [];
    const json = await res.json().catch(() => ({}));
    return Array.isArray(json.owner_ids) ? json.owner_ids : [];
  } catch {
    return [];
  }
}

export function useTodaysSchedule(date) {
  const [schedules, setSchedules] = useState([]);
  const [viewerTimezone, setViewerTimezone] = useState('America/New_York');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selected: selectedProviders } = useSelectedProviders();
  // Stable primitive, so the fetch depends on "did the selection change"
  // rather than on the array identity the hook returns fresh each render —
  // which would refetch forever. Same guard SchedulesTable uses.
  const providersKey = (selectedProviders || []).join(',');

  const fetchSchedules = useCallback(async () => {
    if (!date) return;
    setIsLoading(true);
    setError(null);
    try {
      const { token, userId, role, userRow } = await getSessionInfo();
      // `Accept-Profile` tells PostgREST which schema to read from. Without
      // it, axios queries land in `public` and tenant-schema users (MEDSTUD,
      // STUDENT, SOC_WORK, MISC) see no rows even though their notes-c
      // schedule is populated. supabaseSchema is updated by
      // loadSupabaseSchema() during sign-in.
      const headers = {
        apikey: supabaseKey || '',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(supabaseSchema && supabaseSchema !== 'public'
          ? { 'Accept-Profile': supabaseSchema }
          : {}),
        Accept: 'application/json',
      };

      const params = {
        date_: `eq.${date}`,
        select: '*,transcripts,more_audios',
      };

      if (isPatientRole(role)) {
        if (!userRow?.patient_verified_row_id || userRow?.patient_match_status !== 'verified') {
          setSchedules([]);
          setError(null);
          setIsLoading(false);
          return;
        }
        params.id = `eq.${userRow.patient_verified_row_id}`;
      } else if (userId && !hasClinicalAdminPermissions(role)) {
        const ownerIds = await fetchShareAllOwnerIds(userId);
        const orParts = [
          `user_id.eq.${userId}`,
          ...sharedUsersOverlapOrClauses(userId),
        ];
        if (ownerIds.length > 0) {
          orParts.push(`user_id.in.(${ownerIds.join(',')})`);
        }
        params.or = `(${orParts.join(',')})`;
      }

      // Clinical roles only, matching SchedulesTable: the OR clauses above are
      // scoped to one schema's user ids and would be meaningless across
      // schemas, so non-clinical users keep their single-schema query and the
      // home schema stays the only source.
      const providers = hasClinicalAdminPermissions(role)
        ? (selectedProviders?.length ? selectedProviders : [supabaseSchema])
        : [supabaseSchema];

      const results = await Promise.all(providers.map(async (schema) => {
        try {
          // Per-schema Accept-Profile — this is the whole fan-out. The shared
          // `headers` above carries the HOME schema, so it cannot be reused.
          const res = await axios.get(getSchedulesEndpoint(), {
            headers: {
              ...headers,
              ...(schema && schema !== 'public'
                ? { 'Accept-Profile': schema }
                : { 'Accept-Profile': 'public' }),
            },
            params,
          });
          const data = Array.isArray(res.data) ? res.data : (res.data?.schedules ?? []);
          return { schema, rows: data };
        } catch (e) {
          // One unreadable provider must not empty the sidebar.
          console.warn(`[useTodaysSchedule] provider ${schema} failed:`,
                       e?.message || e);
          return { schema, rows: null };
        }
      }));

      const failed = results.filter((r) => r.rows === null);
      if (failed.length === results.length) {
        throw failed[0]?.error || new Error('Could not load the schedule');
      }
      // `_schema` is what lets the sidebar open a foreign chart against ITS
      // schema — PatientListPanel passes it through to /clinic?schema=.
      const rows = results
        .filter((r) => Array.isArray(r.rows))
        .flatMap((r) => r.rows.map((row) => ({ ...row, _schema: r.schema })));
      const visibleRows = filterRowsForPatient(userRow, rows);
      // Attach each row owner's IANA timezone (timezone_source) and resolve the
      // viewer's timezone, so the sidebar renders times in the viewer's zone —
      // same as SchedulesTable.getSchedules. Degrades to raw rows on failure.
      try {
        const supabase = await getSupabase();
        const [hydrated, vtz] = await Promise.all([
          hydrateScheduleRowsTimezoneSource(supabase, visibleRows),
          fetchViewerIanaTimezone(supabase),
        ]);
        setSchedules(Array.isArray(hydrated) ? hydrated : visibleRows);
        if (vtz) setViewerTimezone(vtz);
      } catch (_) {
        setSchedules(visibleRows);
      }
      setError(null);
    } catch (e) {
      setError(e);
      setSchedules([]);
    } finally {
      setIsLoading(false);
    }
  }, [date, providersKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Refetch on save events that the sidebar's Active/Completed bucketing reads:
  //   - "transcriptSaved"       → App.js (manual save of transcripts)
  //   - "transcriptionSaved"    → AudioRecorder.js (post-recording save)
  //   - "audioRecordingComplete"→ BackupRecoveryPanel.js (restore flow)
  //   - "patientDataRefresh"    → broader catch-all
  useEffect(() => {
    if (!date) return undefined;
    const refresh = () => { fetchSchedules(); };
    window.addEventListener('transcriptSaved', refresh);
    window.addEventListener('transcriptionSaved', refresh);
    window.addEventListener('audioRecordingComplete', refresh);
    window.addEventListener('patientDataRefresh', refresh);
    return () => {
      window.removeEventListener('transcriptSaved', refresh);
      window.removeEventListener('transcriptionSaved', refresh);
      window.removeEventListener('audioRecordingComplete', refresh);
      window.removeEventListener('patientDataRefresh', refresh);
    };
  }, [date, fetchSchedules]);

  return { schedules, viewerTimezone, isLoading, error, refetch: fetchSchedules };
}
